import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";

const ThemeContext = createContext(null);
const STORAGE_KEY = "morfeusz:theme";

const readInitialTheme = () => {
  if (typeof window === "undefined") return "light";
  // 1) URL param wins (backward-compat with old ?theme=dark|old)
  const urlTheme = new URLSearchParams(window.location.search).get("theme");
  if (urlTheme === "dark" || urlTheme === "old") return "dark";
  if (urlTheme === "light") return "light";
  // 2) Persisted choice
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (stored === "dark" || stored === "light") return stored;
  } catch (_) {
    /* ignore */
  }
  // 3) System preference
  if (window.matchMedia && window.matchMedia("(prefers-color-scheme: dark)").matches) {
    return "dark";
  }
  return "light";
};

export const ThemeProvider = ({ children }) => {
  const [theme, setThemeState] = useState(readInitialTheme);

  const setTheme = useCallback((next) => {
    setThemeState(next);
    try {
      window.localStorage.setItem(STORAGE_KEY, next);
    } catch (_) {
      /* ignore */
    }
  }, []);

  const toggleTheme = useCallback(() => {
    setThemeState((prev) => {
      const next = prev === "dark" ? "light" : "dark";
      try {
        window.localStorage.setItem(STORAGE_KEY, next);
      } catch (_) {
        /* ignore */
      }
      return next;
    });
  }, []);

  // Mirror onto <html> + <body> so legacy CSS using [data-theme-variant] keeps working everywhere.
  useEffect(() => {
    if (typeof document === "undefined") return;
    document.documentElement.dataset.themeVariant = theme;
    if (document.body) {
      document.body.dataset.themeVariant = theme;
    }
  }, [theme]);

  const value = useMemo(
    () => ({ theme, isDark: theme === "dark", setTheme, toggleTheme }),
    [theme, setTheme, toggleTheme],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
};

export const useTheme = () => {
  const ctx = useContext(ThemeContext);
  if (!ctx) {
    // Fallback for components rendered outside provider (shouldn't happen in practice).
    return { theme: "light", isDark: false, setTheme: () => {}, toggleTheme: () => {} };
  }
  return ctx;
};
